/* ----------------------------------------------------------------------------
 * active-domain — the shell's active-domain MODEL (Phase 2, Lane 2A).
 *
 * This module is the single, dependency-free source of the active-domain
 * contract the shell consumes (§2.2 of the Phase-2 plan): the camelCase
 * ActiveDomain/DomainOption shapes, the server↔hint reconciliation rule, and
 * the pure presentation helpers the DomainSelector renders.
 *
 * The HTTP read/write path is NOT reinvented here (§2.1: exactly one path).
 * The wire calls live in `@/data/copilot` (Phase-1: getActiveDomain /
 * setActiveDomain, plus the localStorage restore-hint helpers). `useActiveDomain`
 * composes that client with the pure logic below. Keeping this file free of any
 * runtime import (React, api, copilot) is deliberate: the hook logic is unit-
 * testable under `node --test --experimental-strip-types` with no DOM.
 *
 * localStorage is ONLY an instant-paint restore hint, ALWAYS reconciled against
 * the server value — the server wins. Nothing here fabricates a domain; a
 * project_id is a view preference the server re-validates for tenancy.
 * -------------------------------------------------------------------------- */

/** A connected domain in the camelCase shape the shell/hook consume. */
export interface DomainOption {
  projectId: number;
  hostname: string;
  displayName: string;
}

/** The active domain (same shape as an option; null = none pinned). */
export type ActiveDomain = DomainOption;

/** Server snapshot from GET /v1/ai/active-domain, adapted to camelCase. */
export interface ActiveDomainSnapshot {
  active: ActiveDomain | null;
  domains: DomainOption[];
}

/* --- snake_case wire DTOs (mirror @/data/copilot; declared locally so this
 *     module imports nothing at runtime). ---------------------------------- */
export interface DomainScopeDto {
  project_id: number;
  hostname: string;
  display_name: string;
}
export interface ActiveDomainStateDto {
  active: DomainScopeDto | null;
  domains: DomainScopeDto[];
}

export function adaptDomain(d: DomainScopeDto): DomainOption {
  return { projectId: d.project_id, hostname: d.hostname, displayName: d.display_name ?? '' };
}

export function adaptSnapshot(dto: ActiveDomainStateDto): ActiveDomainSnapshot {
  return {
    active: dto.active ? adaptDomain(dto.active) : null,
    domains: Array.isArray(dto.domains) ? dto.domains.map(adaptDomain) : [],
  };
}

/* --------------------------------------------------------------------------
 * Reconciliation — the single rule that turns (server truth + browser hint)
 * into the resolved active domain, whether a choice is still needed, and
 * whether we must persist a silent auto-selection back to the server.
 *
 * Contract:
 *   - 0 domains                         → nothing active, no choice, no persist.
 *   - exactly 1 domain (frictionless)   → auto-select it; persist iff the server
 *                                         has not already recorded it.
 *   - >1 domains, server has an active  → SERVER WINS (a differing hint is
 *                                         ignored); no choice, no persist.
 *   - >1 domains, server active is null:
 *       · a valid browser hint (matches a returned domain) → restore it AND
 *         persist so the server becomes canonical and it survives the next
 *         refresh on any device. A stale/foreign hint never matches the
 *         workspace-scoped domain list, so it is silently ignored (safe).
 *       · otherwise                        → needsChoice: show the chooser.
 * -------------------------------------------------------------------------- */
export interface Reconciliation {
  active: ActiveDomain | null;
  needsChoice: boolean;
  /** project_id to POST so the server records the selection, or null. */
  autoSelectId: number | null;
}

export function reconcileActiveDomain(
  snapshot: ActiveDomainSnapshot,
  localHintId: number | null,
): Reconciliation {
  const { active, domains } = snapshot;

  if (domains.length === 0) {
    return { active: null, needsChoice: false, autoSelectId: null };
  }

  if (domains.length === 1) {
    const only = domains[0];
    const already = active?.projectId === only.projectId;
    return {
      active: active ?? only,
      needsChoice: false,
      autoSelectId: already ? null : only.projectId,
    };
  }

  // Multi-domain.
  if (active) {
    // Server has recorded a choice — it is authoritative, hint is ignored.
    return { active, needsChoice: false, autoSelectId: null };
  }

  // Server has no choice yet: try to restore a valid browser hint.
  const hinted = localHintId != null ? domains.find((d) => d.projectId === localHintId) ?? null : null;
  if (hinted) {
    return { active: hinted, needsChoice: false, autoSelectId: hinted.projectId };
  }

  return { active: null, needsChoice: true, autoSelectId: null };
}

/* --------------------------------------------------------------------------
 * Presentation helpers (rendered by DomainSelector; unit-testable pure).
 * -------------------------------------------------------------------------- */

/** §2.3 label rule: `display_name ? "display_name · hostname" : hostname`. */
export function domainLabel(d: Pick<DomainOption, 'hostname' | 'displayName'>): string {
  const name = (d.displayName ?? '').trim();
  if (name && name !== d.hostname) return `${name} · ${d.hostname}`;
  return d.hostname;
}

export interface SelectorOption {
  projectId: number;
  hostname: string;
  label: string;
  active: boolean;
}

export interface SelectorModel {
  /** false when the workspace has no connected domains → render nothing. */
  visible: boolean;
  /** false when exactly one domain → a static, non-interactive label. */
  interactive: boolean;
  /** the "All domains" workspace-wide lens is offered only for >1 domain. */
  showAllOption: boolean;
  /** true when the workspace-wide ("All domains") lens is selected. */
  allActive: boolean;
  /** true when multi-domain and nothing is resolved yet (prompt the user). */
  needsChoice: boolean;
  /** the trigger label: "All domains" | hostname | "Choose domain". */
  currentLabel: string;
  options: SelectorOption[];
}

export interface SelectorState {
  domains: DomainOption[];
  active: ActiveDomain | null;
  isAllDomains: boolean;
  needsChoice: boolean;
}

export function buildSelectorModel(state: SelectorState): SelectorModel {
  const { domains, active, isAllDomains, needsChoice } = state;
  const multi = domains.length > 1;

  let currentLabel: string;
  if (isAllDomains) currentLabel = 'All domains';
  else if (active) currentLabel = active.hostname;
  else if (domains.length === 1) currentLabel = domains[0].hostname;
  else currentLabel = 'Choose domain';

  return {
    visible: domains.length > 0,
    interactive: multi,
    showAllOption: multi,
    allActive: isAllDomains,
    needsChoice: multi && needsChoice && !isAllDomains,
    currentLabel,
    options: domains.map((d) => ({
      projectId: d.projectId,
      hostname: d.hostname,
      label: domainLabel(d),
      active: !isAllDomains && active?.projectId === d.projectId,
    })),
  };
}
